/**
 * История: поездки, стоянки, сводка и трек на карте.
 *
 * Период выбирается кнопками, а не двумя календарями: диспетчеру почти всегда
 * нужны сутки, неделя или месяц, а произвольные даты — редкий случай, ради
 * которого не стоит усложнять ежедневную работу.
 *
 * Пределы по объёму периода живут на сервере. Здесь их не дублируем: вторая
 * копия правила разошлась бы с первой, а отказ и так приходит понятным текстом.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  api,
  type Device,
  type Position,
  type StopRow,
  type SummaryRow,
  type TripRow,
} from './api'
import { dateTime, duration, km, kmh } from './format'
import { dict, type Lang } from './i18n'
import { title } from './vehicleId'
import { downloadCsv, reportCsv, reportFilename } from './reportExport'
import { believable } from './summary'

type Range = '24h' | '7d' | '30d'
type View = 'trips' | 'stops' | 'summary'

const HOURS: Record<Range, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }

type Props = {
  device: Device
  lang: Lang
  track: Position[] | null
  /** Предел глубины трека в сутках — приходит с сервера. */
  trackDays: number
  onTrack: (points: Position[] | null) => void
}

export function History({ device, lang, track, onTrack, trackDays }: Props) {
  const t = dict(lang)
  const distance = (meters: number) => `${km(meters, lang)} ${t.unitKm}`
  const [range, setRange] = useState<Range>('24h')
  const [view, setView] = useState<View>('trips')
  const [rows, setRows] = useState<unknown[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Сутки в выбранном промежутке — сравниваем с пределом сервера.
  const trackAllowed = HOURS[range] / 24 <= trackDays

  const bounds = useCallback(() => {
    const until = new Date()
    const since = new Date(until.getTime() - HOURS[range] * 3600_000)
    return { since, until }
  }, [range])

  useEffect(() => {
    let cancelled = false
    setBusy(true)
    setError('')
    const { since, until } = bounds()
    api
      .report<unknown[]>(view, [device.id], since, until)
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((caught) => {
        if (cancelled) return
        // Отказ по объёму приходит с готовым текстом — показываем его целиком.
        setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
        setRows(null)
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [device.id, view, bounds, t.networkError])

  // Трек снимаем при смене машины: показывать чужой маршрут нельзя.
  useEffect(() => {
    onTrack(null)
  }, [device.id, onTrack])

  /**
   * Выгрузка собирается из уже показанных строк, а не новым запросом: файл
   * обязан совпадать с тем, что человек видит на экране. Второй запрос за то
   * же время вернул бы другие данные — за секунды между ними машина успевает
   * закончить поездку.
   */
  function save(kind: 'trips' | 'stops' | 'summary' | 'route', data: unknown[]) {
    const { since, until } = bounds()
    const text = reportCsv(kind, data, {
      lang,
      nameById: new Map([[device.id, title(device)]]),
    })
    downloadCsv(reportFilename(kind, since, until, title(device)), text)
  }

  async function toggleTrack() {
    if (track) {
      onTrack(null)
      return
    }
    setBusy(true)
    setError('')
    try {
      const { since, until } = bounds()
      onTrack(await api.report<Position[]>('route', [device.id], since, until))
    } catch (caught) {
      setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="history">
      <div className="chips" role="group" aria-label={t.period}>
        {(['24h', '7d', '30d'] as const).map((key) => (
          <button
            key={key}
            aria-pressed={range === key}
            onClick={() => {
              setRange(key)
              onTrack(null) // старый трек относится к другому периоду
            }}
          >
            {key === '24h' ? t.last24h : key === '7d' ? t.last7d : t.last30d}
          </button>
        ))}
      </div>

      {/* Трек сервер отдаёт не глубже своего предела. Предлагать кнопку,
          которая заведомо получит отказ, — значит учить человека, что
          приложение иногда не работает. Лучше сказать заранее. */}
      <button
        className="act"
        onClick={toggleTrack}
        disabled={busy || !trackAllowed}
        title={trackAllowed ? undefined : t.trackTooLong(trackDays)}
      >
        {track ? t.hideTrack : t.showTrack}
      </button>

      {!trackAllowed && <p className="hint">{t.trackTooLong(trackDays)}</p>}

      {track && track.length > 0 && (
        <button className="act ghost" onClick={() => save('route', track)}>
          {t.exportTrack}
        </button>
      )}

      <div className="chips" role="tablist">
        {(['trips', 'stops', 'summary'] as const).map((key) => (
          <button
            key={key}
            role="tab"
            aria-selected={view === key}
            onClick={() => setView(key)}
          >
            {key === 'trips' ? t.trips : key === 'stops' ? t.stops : t.summary}
          </button>
        ))}
      </div>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {busy && !error && <p className="hint">{t.loading}</p>}

      {!busy && !error && rows?.length === 0 && <p className="hint">{t.nothingFound}</p>}

      {!busy && !error && rows && rows.length > 0 && (
        <>
          <div className="rows">
            {view === 'trips' &&
              (rows as TripRow[]).map((row, i) => (
                <div className="rec" key={i}>
                  <div className="rec-top">
                    <b>
                      {dateTime(row.startTime, lang)} → {dateTime(row.endTime, lang)}
                    </b>
                    <span>{distance(row.distance)}</span>
                  </div>
                  <div className="rec-sub">
                    {t.avgSpeed}{' '}
                    {believable(row, 'averageSpeed') ? kmh(row.averageSpeed, lang) : '—'} ·{' '}
                    {t.maxSpeed}{' '}
                    {believable(row, 'maxSpeed') ? `${kmh(row.maxSpeed, lang)} ${t.speed}` : '—'}
                  </div>
                </div>
              ))}

            {view === 'stops' &&
              (rows as StopRow[]).map((row, i) => (
                <div className="rec" key={i}>
                  <div className="rec-top">
                    <b>
                      {dateTime(row.startTime, lang)} → {dateTime(row.endTime, lang)}
                    </b>
                    <span>{duration(row.duration, lang, t)}</span>
                  </div>
                  {row.address && <div className="rec-sub">{row.address}</div>}
                </div>
              ))}

            {view === 'summary' &&
              (rows as SummaryRow[]).map((row, i) => (
                <div className="rec" key={i}>
                  <div className="rec-top">
                    <b>{t.distance}</b>
                    <span>{distance(row.distance)}</span>
                  </div>
                  <div className="rec-top">
                    <b>{t.engineHours}</b>
                    <span>
                      {believable(row, 'engineHours') ? duration(row.engineHours, lang, t) : '—'}
                    </span>
                  </div>
                  <div className="rec-sub">
                    {t.avgSpeed}{' '}
                    {believable(row, 'averageSpeed') ? kmh(row.averageSpeed, lang) : '—'} ·{' '}
                    {t.maxSpeed}{' '}
                    {believable(row, 'maxSpeed') ? `${kmh(row.maxSpeed, lang)} ${t.speed}` : '—'}
                  </div>
                </div>
              ))}
          </div>

          <button className="act ghost" onClick={() => save(view, rows)}>
            {t.exportAction}
          </button>
        </>
      )}
    </div>
  )
}
